/**
 * TODO: make template-only component,
 * and use class-based modifier?
 *
 * This would require that modifiers could run pre-render
 */ import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { assert } from '@ember/debug';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { handle } from '../proper-links.ts';
import { ExternalLink } from './external-link.gts';
import type RouterService from '@ember/routing/router-service';
export interface Signature {
    Element: HTMLAnchorElement;
    Args: {
        /**
     * the `href` string value to set on the anchor element.
     */ href: string;
        /**
     * When calculating the "active" state of the link, you may decide
     * whether or not you want to _require_ that all query params be considered (true)
     * or specify individual query params, ignoring anything not specified.
     *
     * For example:
     *
     * ```gjs live preview
     * import { Link } from 'ember-primitives';
     *
     * <template>
     *   <Link @href="/" @includeActiveQueryParams={{true}} as |a|>
     *     ...
     *   </Link>
     * </template>
     * ```
     *
     * the data-active state here will only be "true" on
     * - `/`
     * - `/?foo=2`
     * - `/?foo=&bar=`
     *
     */ includeActiveQueryParams?: true | string[];
    };
    Blocks: {
        default: [{
                /**
         * Indicates if the passed `href` is pointing to an external site.
         * Useful if you want your links to have additional context for when
         * a user is about to leave your site.
         *
         * For example:
         *
         * ```gjs live preview
         * import { Link } from 'ember-primitives';
         *
         * const MyLink = <template>
         *   <Link @href={{@href}} as |a|>
         *     {{yield}}
         *     {{#if a.isExternal}}
         *       ➚
         *     {{/if}}
         *   </Link>
         * </template>;
         *
         * <template>
         *   <MyLink @href="https://developer.mozilla.org">MDN</MyLink> &nbsp;&nbsp;
         *   <MyLink @href="/">Home</MyLink>
         *  </template>
         * ```
         */ isExternal: boolean;
                /**
         * Indicates if the passed `href` is *active*, or the user is on the same basepath.
         * This allows consumers to style their link if they wish or style their text.
         * The active state will also be present on a `data-active` attribute on the generated anchor tag.
         *
         *
         * For example
         * ```gjs
         * import { Link, service } from 'ember-primitives';
         *
         * const MyLink = <template>
         *   <Link @href="..."> as |a|>
         *     <span class="{{if a.isActive 'underline'}}">
         *     {{yield}}
         *     </span>
         *   </Link>
         * </template>
         *
         * <template>
         * {{#let (service 'router') as |router|}}
         *     <MyLink @href={{router.currentURL}}>Ths page</MyLink> &nbsp;&nbsp;
         *     <MyLink @href="/">Home</MyLink>
         *   {{/let}}
         *  </template>
         * ```
         *
         * By default, the query params are omitted from `isActive` calculation, but you may
         * configure the query params to be included if you wish
         * See: `@includeActiveQueryParams`
         *
         * Note that external links are never active.
         */ isActive: boolean;
            }];
    };
}
/**
 * A light wrapper around the [Anchor element][mdn-a], which will appropriately make your link an external link if the passed `@href` is not on the same domain.
 *
 *
 * [mdn-a]: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a
 */ export class Link extends Component<Signature> {
    static{
        template(`
    {{#if (isExternal @href)}}
      <ExternalLink href={{@href}} ...attributes>
        {{yield (hash isExternal=true isActive=false)}}
      </ExternalLink>
    {{else}}
      <a
        data-active={{this.isActive}}
        href={{if @href @href "##missing##"}}
        {{on "click" this.handleClick}}
        ...attributes
      >
        {{yield (hash isExternal=false isActive=this.isActive)}}
      </a>
    {{/if}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
    @service
    router: RouterService;
    handleClick = (event1: MouseEvent)=>{
        assert('[BUG]', event1.target instanceof HTMLAnchorElement);
        handle(this.router, event1.target, [], event1);
    };
    get isActive() {
        let { href: href1, includeActiveQueryParams: includeActiveQueryParams1 } = this.args;
        return isActive(this.router, href1, includeActiveQueryParams1);
    }
}
export default Link;
function isExternal(href1: string) {
    if (!href1) return false;
    if (href1.startsWith('#')) return false;
    if (href1.startsWith('/')) return false;
    return location.origin !== new URL(href1).origin;
}
function isActive(router1: RouterService, href1: string, includeQueryParams1?: boolean | string[]) {
    if (!includeQueryParams1) {
        /**
     * is Active doesn't understand `href`, so we have to convert to RouteInfo-esque
     */ let info1 = router1.recognize(href1);
        if (info1) {
            let dynamicSegments1 = getParams(info1);
            return router1.isActive(info1.name, ...dynamicSegments1);
        }
        return false;
    }
    let url1 = new URL(href1, location.origin);
    let hrefQueryParams1 = new URLSearchParams(url1.searchParams);
    let hrefPath1 = url1.pathname;
    const currentPath1 = router1.currentURL?.split('?')[0];
    if (!currentPath1) return false;
    if (hrefPath1 !== currentPath1) return false;
    const currentQueryParams1 = router1.currentRoute?.queryParams;
    if (!currentQueryParams1) return false;
    if (includeQueryParams1 === true) {
        return Object.entries(currentQueryParams1).every(([key1, value1])=>{
            return hrefQueryParams1.get(key1) === value1;
        });
    }
    return includeQueryParams1.every((key1)=>{
        return hrefQueryParams1.get(key1) === currentQueryParams1[key1];
    });
}
type RouteInfo = ReturnType<RouterService['recognize']>;
function getParams(currentRouteInfo1: RouteInfo) {
    let params1: Record<string, string | unknown | undefined>[] = [];
    while(currentRouteInfo1?.parent){
        let currentParams1 = currentRouteInfo1.params;
        params1 = currentParams1 ? [
            currentParams1,
            ...params1
        ] : params1;
        currentRouteInfo1 = currentRouteInfo1.parent;
    }
    return params1.map(Object.values).flat();
}
